import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  loginData: null,
  authData: undefined, // Added for Redux Persist compatibility
  isLoggedIn: false,
  featureUrlList: null, // Stored separately — only set from signin, never overwritten by isLogin
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload;
    },
    setAuthData: (state, action) => {
      const newData = action.payload;
      if (newData) {
        // Determine if this is a signin response (has nested user.loggedInUserDetail)
        // or an isLogin response (flat user object with screenName at top level)
        const isSigninResponse = !!newData?.user?.loggedInUserDetail;

        if (isSigninResponse) {
          // Signin response — store full response, extract featuresUrlList
          state.authData = newData;
          state.user = newData.user; // { loggedInUserDetail, featuresUrlList, ... }
          state.featureUrlList = newData.user.featuresUrlList || null;
          state.loginData = newData.user.loggedInUserDetail || null;
        } else {
          // isLogin response — update everything with fresh data, preserve only featuresUrlList
          state.loginData = newData;

          // Build the user object in the shape the sell app expects
          // sell app reads user.userType, user.sellerId etc. at top level
          const preservedFeaturesList = state.featureUrlList || state.user?.featuresUrlList || null;

          // Spread isLogin fields to top level of user (sell app reads user.userType directly)
          // Keep loggedInUserDetail and featuresUrlList as nested fields
          state.user = {
            ...newData,
            loggedInUserDetail: newData,
            featuresUrlList: preservedFeaturesList,
          };

          // Update authData to reflect current state
          state.authData = {
            ...(state.authData || {}),
            user: state.user,
          };
        }

        state.isLoggedIn = true;
        state.isAuthenticated = true;
      } else {
        state.authData = newData;
      }
    },
    clearAuthData: () => {
      // Return fresh initialState to remove ALL fields (including stale persisted ones)
      return { ...initialState };
    },
    setLoginData: (state, action) => {
      state.loginData = action.payload;
    },
    setAuthLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setAuthError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const userDetailsSlice = createSlice({
  name: "userDetails",
  initialState,
  reducers: {
    setUserDetailsSlices: (state, action) => {
      state.userDetailsSlice = action.payload;
    },
    clearUserDetailsSlice: (state) => {
      state.userDetailsSlice = null;
    },
  },
});

export const { setAuthenticated, setAuthData, clearAuthData, setLoginData, setAuthLoading, setAuthError } =
  authSlice.actions;
export const { setUserDetailsSlices, clearUserDetailsSlice } =
  userDetailsSlice.actions;

export default authSlice.reducer;
